import { isProviderError, providerErrorSummary } from "./provider-error";
export type SafeLogAttribute = string | number | boolean;
export type SafeLogAttributes = Record<string, SafeLogAttribute>;

const sensitiveKeyPattern =
  /(authorization|cookie|email|message|password|prompt|secret|token|content)/i;
const secretValuePattern =
  /(bearer\s+\S+|(?:token|password|secret)=\S+|eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/gi;
const errorContextKeys = new Set([
  "attempt",
  "code",
  "error",
  "model",
  "name",
  "status",
  "statusCode",
]);
const allowedSensitiveKeys = new Set(["error_message", "error_stack"]);

export function redactLogText(value: string) {
  return value.replace(secretValuePattern, "[REDACTED]").slice(0, 2_000);
}

export function sanitizeLogAttributes(
  attributes: Record<string, unknown> = {},
): SafeLogAttributes {
  const safeAttributes: SafeLogAttributes = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (!allowedSensitiveKeys.has(key) && sensitiveKeyPattern.test(key)) {
      continue;
    }

    if (typeof value === "string") {
      safeAttributes[key] = redactLogText(value);
    } else if (typeof value === "number" && Number.isFinite(value)) {
      safeAttributes[key] = value;
    } else if (typeof value === "boolean") {
      safeAttributes[key] = value;
    }
  }

  return safeAttributes;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object") {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function getArgumentAttributes(args: unknown[]) {
  const attributes: Record<string, unknown> = {};

  for (const arg of args) {
    if (isProviderError(arg)) {
      Object.assign(attributes, providerErrorSummary(arg));
      continue;
    }
    if (arg instanceof Error) {
      attributes.error_name = arg.name;
      attributes.error_message = arg.message;
      attributes.error_stack = arg.stack;

      const errorCode = (arg as Error & { code?: unknown }).code;
      if (errorCode !== undefined) {
        attributes.error_code = errorCode;
      }
      continue;
    }

    if (!isPlainRecord(arg)) {
      continue;
    }

    const hasErrorContext = Object.keys(arg).some((key) =>
      errorContextKeys.has(key),
    );

    for (const [key, value] of Object.entries(arg)) {
      if (key === "message" && hasErrorContext) {
        attributes.error_message = value;
      } else {
        attributes[key] = value;
      }
    }
  }

  return sanitizeLogAttributes(attributes);
}

function appendAttributesToBody(
  body: string,
  attributes: SafeLogAttributes,
) {
  const summary = Object.fromEntries(
    Object.entries(attributes).filter(([key]) => key !== "error_stack"),
  );

  return Object.keys(summary).length > 0
    ? redactLogText(`${body} ${JSON.stringify(summary)}`)
    : redactLogText(body);
}

export function getConsoleLogRecord(
  level: "warn" | "error",
  args: unknown[],
) {
  const firstString = args.find((arg): arg is string => typeof arg === "string");
  const attributes = getArgumentAttributes(args);
  const baseBody =
    firstString ?? (level === "error" ? "Runtime error" : "Runtime warning");

  return {
    body: appendAttributesToBody(baseBody, attributes),
    attributes,
  };
}

export function sanitizeProviderLogArguments(args: unknown[]) {
  return args.map(arg => isProviderError(arg) ? providerErrorSummary(arg) : arg);
}
